import React, { useState } from 'react';
import StatusBadge from '../shared/StatusBadge';
import A2PCampaignModal from './A2PCampaignModal';
import './SmsComplianceCards.css';

const A2PCampaignCard = ({ userId, campaignData, brandData, onUpdate, disabled = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasCampaign = campaignData && campaignData.id;
  const hasBrand = brandData && brandData.id;

  const isBrandApproved = brandData?.status &&
    ['approved', 'twilio-approved', 'verified'].includes(brandData.status);

  const isCampaignApproved = campaignData?.status &&
    ['approved', 'twilio-approved', 'verified'].includes(campaignData.status);

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { badge: 'draft', label: 'Draft' },
      'pending': { badge: 'pending', label: 'Pending Review' },
      'pending-review': { badge: 'pending', label: 'Pending Review' },
      'in-review': { badge: 'pending', label: 'In Review' },
      'approved': { badge: 'approved', label: 'Approved' },
      'twilio-approved': { badge: 'approved', label: 'Approved' },
      'rejected': { badge: 'rejected', label: 'Rejected' },
      'twilio-rejected': { badge: 'rejected', label: 'Rejected' },
      'verified': { badge: 'approved', label: 'Approved' },
    };
    return statusMap[status] || { badge: 'draft', label: status };
  };

  const getHeaderColorClass = (status) => {
    const normalizedStatus = status ? status.toLowerCase() : '';
    if (['twilio-approved', 'approved', 'verified'].includes(normalizedStatus)) return 'card-header--approved';
    if (['pending-review', 'pending', 'in-review'].includes(normalizedStatus)) return 'card-header--pending';
    if (['twilio-rejected', 'rejected'].includes(normalizedStatus)) return 'card-header--rejected';
    if (['draft'].includes(normalizedStatus)) return 'card-header--draft';
    return 'card-header--default';
  };

  const getButtonText = () => {
    if (!hasCampaign) return 'Create Campaign';
    if (isCampaignApproved) return 'View';
    return 'Edit Campaign';
  };

  const handleModalClose = async (updated) => {
    setShowModal(false);
    if (updated) {
      setIsRefreshing(true);
      await onUpdate();
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1500);
    }
  };

  const handleButtonClick = () => {
    if (!hasBrand) {
      alert('Please register your brand first before creating a campaign');
      return;
    }
    if (!isBrandApproved && !hasCampaign) {
      alert('Your brand must be approved before you can create a campaign');
      return;
    }
    setShowModal(true);
  };

  return (
    <>
      <div className="sms-compliance-card">
        {/* Header with Icon and Title */}
        <div className={`card-header ${hasCampaign ? getHeaderColorClass(campaignData.status) : ''}`}>
          <div className="card-icon-title">
            {/* <i className="la la-bullhorn" style={{ fontSize: '20px', color: '#087ab6',fontFamily:'LineAwesome' }}></i> */}
            <h3 className="card-title">A2P Campaign Registration</h3>
          </div>
          {hasCampaign && (
            <span className="status-text">{getStatusBadge(campaignData.status).label}</span>
          )}
        </div>

        {/* Card Body with Message and Button */}
        <div className="card-body" style={{ padding: '30px 20px', textAlign: 'center' }}>
          {/* Status Message */}
          {!hasBrand || !isBrandApproved ? (
            <p style={{ color: '#ffc107', marginBottom: '20px', fontSize: '14px' }}>
              Brand approval required
            </p>
          ) : hasCampaign ? (
            <>
              {isCampaignApproved && (
                <p style={{ color: '#28a745', marginBottom: '20px', fontSize: '14px' }}>
                  Campaign approved - You can now assign phone numbers
                </p>
              )}

              {['pending', 'pending-review', 'in-review'].includes(campaignData.status) && (
                <p style={{ color: '#ffc107', marginBottom: '20px', fontSize: '14px' }}>
                  Pending review (typically 1-3 business days)
                </p>
              )}

              {['rejected', 'twilio-rejected'].includes(campaignData.status) && (
                <p style={{ color: '#dc3545', marginBottom: '20px', fontSize: '14px' }}>
                  Registration rejected. Please review and resubmit.
                </p>
              )}

              {campaignData.status === 'draft' && (
                <p style={{ color: '#6c757d', marginBottom: '20px', fontSize: '14px' }}>
                  Draft saved. Complete and submit for review.
                </p>
              )}
            </>
          ) : (
            <p style={{ color: '#6c757d', marginBottom: '20px', fontSize: '14px' }}>
              Complete your A2P campaign registration to start sending SMS messages.
            </p>
          )}

          {/* Button */}
          <button
            className="btn linear-gradient zy-save-btn"
            onClick={handleButtonClick}
            disabled={!hasBrand || !isBrandApproved}
            style={{
              opacity: (!hasBrand || !isBrandApproved) ? 0.5 : 1,
              cursor: (!hasBrand || !isBrandApproved) ? 'not-allowed' : 'pointer'
            }}
          >
            {getButtonText()}
          </button>

          {/* Refreshing Overlay */}
          {isRefreshing && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
              borderRadius: '12px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div className="ui active inline loader" style={{ marginBottom: '10px' }}></div>
                <p style={{ color: '#087ab6', fontSize: '13px', fontWeight: 500, margin: 0 }}>
                  Refreshing data...
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* A2P Campaign Modal */}
      {showModal && (
        <A2PCampaignModal
          userId={userId}
          campaignData={campaignData}
          brandData={brandData}
          isEditMode={hasCampaign}
          isApproved={isCampaignApproved}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

export default A2PCampaignCard;
